import React, { useState, useEffect, useRef } from 'react';
import Cleave from 'cleave.js/react';
import cn from 'classnames';

import Label from '../components/Label';
import FormError from '../components/FormError';
import FormHint from '../components/FormHint';

import { colors } from '../config';

const Input = ({
  id,
  label,
  hint,
  error,
  readOnly = false,
  register,
  mask,
  name,
  defaultValue,
  watch,
  triggerValidation,
  focusOnMount = false,
  customMask,
  customError,
  onChange,
  ...rest
}) => {
  const [value, setValue] = useState(defaultValue || '');
  const [dirty, setDirty] = useState(false);

  const onCustomChange = event => {
    if (customMask) {
      const raw = event.target.rawValue.replace(/\./g, '').replace(/\-/g, '');
      setValue(raw);
      watch && watch(raw);
    } else {
      setValue(event.target.rawValue);
      watch && watch(event.target.rawValue);
    }
  };

  const onCustomBlur = () => {
    setDirty(true);
    triggerValidation && triggerValidation();
  };

  useEffect(() => {
    if (value !== '' && dirty) {
      triggerValidation && triggerValidation();
    }
  }, [triggerValidation, value, dirty]);

  const inputRef = useRef(null);

  useEffect(() => {
    focusOnMount && inputRef.current.focus();
  }, [focusOnMount]);

  return (
    <div className={cn('input', { 'input-readonly': readOnly })}>
      <Label htmlFor={id}>{label}</Label>

      {mask ? (
        <>
          <Cleave
            options={mask}
            onChange={e => {
              onCustomChange(e);
              onChange && onChange(e);
            }}
            onBlur={onCustomBlur}
            value={customMask ? customMask : value}
            htmlRef={e => {
              if (focusOnMount) {
                inputRef.current = e;
              }
            }}
            {...rest}
          />

          <input id={id} name={name} ref={register} value={value} {...rest} type="hidden" />
        </>
      ) : (
        <input
          id={id}
          name={name}
          readOnly={readOnly}
          ref={e => {
            register && register(e);

            if (focusOnMount) {
              inputRef.current = e;
            }
          }}
          defaultValue={defaultValue}
          {...rest}
        />
      )}

      {hint && <FormHint>{hint}</FormHint>}
      {error && <FormError>{error.message}</FormError>}

      <style jsx>{`
        .input {
          position: relative;
          margin-bottom: 32px;
        }

        .input :global(> input) {
          background: transparent;
          border: 0;
          border-bottom: 1px solid ${colors.inputBorderColor};
          border-radius: 0;
          display: block;
          font-family: inherit;
          font-size: 16px;
          padding: 8px 0 8px 0;
          transition: border-bottom-color 0.5s ease;
          width: 100%;
        }

        input:focus,
        input:active {
          border-bottom-color: ${colors.orange};
          /* outline: 0; */
        }

        .input-readonly {
          border-bottom: 1px solid ${colors.lighterGrey};
          margin-bottom: 24px;
          padding-bottom: 18px;
        }

        input[readonly] {
          border-bottom: 0;
          color: ${colors.baseTextColor};
          font-size: 18px;
        }

        input[readonly]:focus {
          outline: 0;
        }

        .input-readonly :global(label) {
          color: ${colors.textMuted};
          font-weight: bold;
        }

        .input :global(> input:disabled) {
          opacity: 1 !important;
          -webkit-text-fill-color: rgba(166, 161, 158, 1) !important;
          color: rgba(166, 161, 158, 1) !important;
        }
      `}</style>
      {customError && customError}
    </div>
  );
};

export default Input;
